/**
 * The app's one rule for a "go here afterwards" target that arrives from
 * outside the code: a `?redirect=` search param, a value carried through the
 * onboarding gate.
 *
 * The check is semantic, not textual. A string test for `//` or `\` is bypassed
 * by whatever the URL parser tolerates but the test does not know about: the
 * WHATWG parser deletes tabs and newlines and reads `\` as `/`, so `/\t/evil.com`
 * sails past a `startsWith("//")` check and then *becomes* `//evil.com`. So the
 * target is resolved against the app's own origin exactly as the browser would
 * resolve it, and accepted only if it stays there.
 */

import { HOME_PATH } from "./routes";

/**
 * Resolve `target` to an in-app `pathname + search + hash`, or `fallback` when
 * it is missing, malformed, or points anywhere but this origin.
 *
 * The result is always a rooted path that is safe to hand to
 * `router.navigate({ href })`: no scheme, no host, and never starting `//`
 * (which `navigate` would otherwise have to guess was a path rather than a
 * protocol-relative URL).
 */
export function safeRedirectTarget(
  target: unknown,
  fallback: string = HOME_PATH
): string {
  if (typeof target !== "string" || target === "") return fallback;

  let resolved: URL;
  try {
    resolved = new URL(target, window.location.origin);
  } catch {
    return fallback;
  }

  if (resolved.origin !== window.location.origin) return fallback;

  const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  // `/.//evil.com` normalises to a same-origin `//evil.com` path. Harmless to
  // the parser, but ambiguous to every consumer that sees only the string.
  if (path.startsWith("//")) return fallback;

  return path;
}
